import { createSlice } from '@reduxjs/toolkit';
import { ErrorType, RootState } from '../../../app/store';
import { NotificationDto } from '@inosio/payments-sdk';

type notificationSettingsState = {
  // fetching all supported notifications for this user
  isFetchingSupportedNotifications: boolean;
  isFetchingSupportedNotificationsSuccess: boolean;
  isFetchingSupportedNotificationsFailed: boolean;
  supportedNotifications: NotificationDto[];
  fetchSupportedNotificationsError: ErrorType | null;

  // creating a notification subscription for this user
  isCreatingNotificationSubscriptionFailed: boolean;
  isCreatingNotificationSubscriptionSuccess: boolean;
  isCreatingNotificationSubscription: boolean;
  createNotificationSubscriptionError: ErrorType | null;

  // deleting a notification subscription for this user
  isDeletingNotificationSubscriptionFailed: boolean;
  isDeletingNotificationSubscriptionSuccess: boolean;
  isDeletingNotificationSubscription: boolean;
  deleteNotificationSubscriptionError: ErrorType | null;
};

const getInitialState = (): notificationSettingsState => ({
  // fetching all supported notifications for this user
  isFetchingSupportedNotifications: false,
  isFetchingSupportedNotificationsSuccess: false,
  isFetchingSupportedNotificationsFailed: false,
  supportedNotifications: [],
  fetchSupportedNotificationsError: null,

  // creating a notification subscription for this user
  isCreatingNotificationSubscriptionFailed: false,
  isCreatingNotificationSubscriptionSuccess: false,
  isCreatingNotificationSubscription: false,
  createNotificationSubscriptionError: null,

  // deleting a notification subscription for this user
  isDeletingNotificationSubscriptionFailed: false,
  isDeletingNotificationSubscriptionSuccess: false,
  isDeletingNotificationSubscription: false,
  deleteNotificationSubscriptionError: null,
});

export const notificationSettingsSlice = createSlice({
  name: 'notificationSettingsSlice',
  initialState: getInitialState(),
  reducers: {
    //fetch all supported notifications
    getSupportedNotifications(state, action) {
      state.isFetchingSupportedNotifications = true;
      state.isFetchingSupportedNotificationsSuccess = false;
      state.isFetchingSupportedNotificationsFailed = false;
      state.fetchSupportedNotificationsError = null;
    },
    setSupportedNotificationsData(state, action) {
      state.isFetchingSupportedNotifications = false;
      state.isFetchingSupportedNotificationsSuccess = true;
      state.isFetchingSupportedNotificationsFailed = false;
      state.supportedNotifications = action.payload;
    },
    setSupportedNotificationsError(state, action) {
      state.isFetchingSupportedNotifications = false;
      state.isFetchingSupportedNotificationsSuccess = false;
      state.isFetchingSupportedNotificationsFailed = true;
      state.fetchSupportedNotificationsError = action.payload;
    },
    //create notification subscription
    createNotificationSubscription(state, action) {
      state.isCreatingNotificationSubscription = true;
      state.isCreatingNotificationSubscriptionSuccess = false;
      state.isCreatingNotificationSubscriptionFailed = false;
      state.createNotificationSubscriptionError = null;
    },
    setCreateNotificationSubscription(state) {
      state.isCreatingNotificationSubscriptionSuccess = true;
      state.isCreatingNotificationSubscription = false;
      state.isCreatingNotificationSubscriptionFailed = false;
      state.createNotificationSubscriptionError = null;
    },
    setCreateNotificationSubscriptionError(state, action) {
      state.isCreatingNotificationSubscriptionSuccess = false;
      state.isCreatingNotificationSubscription = false;
      state.isCreatingNotificationSubscriptionFailed = true;
      state.createNotificationSubscriptionError = action.payload;
    },
    //delete notification subscription
    deleteNotificationSubscription(state) {
      state.isCreatingNotificationSubscription = true;
      state.isCreatingNotificationSubscriptionSuccess = false;
      state.isCreatingNotificationSubscriptionFailed = false;
      state.createNotificationSubscriptionError = null;
    },
    setDeleteNotificationSubscription(state) {
      state.isCreatingNotificationSubscriptionSuccess = true;
      state.isCreatingNotificationSubscription = false;
      state.isCreatingNotificationSubscriptionFailed = false;
      state.createNotificationSubscriptionError = null;
    },
    setDeleteNotificationSubscriptionError(state, action) {
      state.isCreatingNotificationSubscriptionSuccess = false;
      state.isCreatingNotificationSubscription = false;
      state.isCreatingNotificationSubscriptionFailed = true;
      state.createNotificationSubscriptionError = action.payload;
    },
  },
});

export const {
  getSupportedNotifications,
  setSupportedNotificationsError,
  createNotificationSubscription,
  setCreateNotificationSubscription,
  setCreateNotificationSubscriptionError,
  setDeleteNotificationSubscription,
  setDeleteNotificationSubscriptionError,
  setSupportedNotificationsData,
  deleteNotificationSubscription,
} = notificationSettingsSlice.actions;

export default notificationSettingsSlice.reducer;

export const selectNotificationSettingsState = (state: RootState) => state.notificationSettingsState;
